import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Generic resource picker: fetches /api/admin/resources/{kind} once + lets user pick one (single)
// or many (multi). Returns the resource id(s).
//
// Props:
//   kind         — resource kind (e.g., "queues", "skills", "music_on_hold")
//   value        — string (single) or string[] (multi). null/[] for empty.
//   onChange     — receives the new value
//   multi        — boolean
//   placeholder  — string
//   testId       — string for data-testid
//   filter       — optional fn(row)=>bool
//   labelKey     — defaults to "name"
//
// Kinds that live under /api/admin/catalogs/ rather than /api/admin/resources/.
// These are the picklist-style catalogs managed in their own admin pages
// (Dispositions, Pause Codes, Wrap-up Codes).
const CATALOG_KINDS = new Set(["dispositions", "pause-codes", "wrapup-codes"]);

export default function RelationalPicker({ kind, value, onChange, multi = false, placeholder = "Select…", testId, filter, labelKey = "name", returnKey = "id" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const base = CATALOG_KINDS.has(kind) ? "catalogs" : "resources";
        const r = await api.get(`/admin/${base}/${kind}`);
        if (!cancelled) setItems(r.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [kind]);

  const filtered = useMemo(() => filter ? items.filter(filter) : items, [items, filter]);
  const keyOf = (row) => (returnKey === "name" ? row.name : row.id);
  const byKey = useMemo(() => Object.fromEntries(filtered.map((x) => [keyOf(x), x])), [filtered, returnKey]);

  function toggle(key) {
    if (multi) {
      const cur = Array.isArray(value) ? value : [];
      onChange(cur.includes(key) ? cur.filter((v) => v !== key) : [...cur, key]);
    } else {
      onChange(value === key ? "" : key);
      setOpen(false);
    }
  }

  const selectedLabel = !multi && value ? (byKey[value]?.[labelKey] || value) : null;

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button data-testid={testId} variant="outline" role="combobox" className="w-full justify-between h-9 font-normal">
            <span className="truncate">
              {multi
                ? (Array.isArray(value) && value.length > 0
                    ? `${value.length} selected`
                    : <span className="text-muted-foreground">{placeholder}</span>)
                : (selectedLabel || <span className="text-muted-foreground">{placeholder}</span>)}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={loading ? "Loading…" : `Search ${kind.replace(/_/g, " ")}…`} />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {filtered.map((it) => {
                  const k = keyOf(it);
                  const selected = multi ? (Array.isArray(value) && value.includes(k)) : value === k;
                  return (
                    <CommandItem
                      key={it.id}
                      value={it[labelKey] || it.id}
                      onSelect={() => toggle(k)}
                      data-testid={`${testId}-opt-${it.id}`}
                    >
                      <Check className={`mr-2 h-3.5 w-3.5 ${selected ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{it[labelKey] || it.id}</span>
                      {it.is_active === false && <Badge variant="outline" className="ml-auto text-[10px]">off</Badge>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected chips for multi */}
      {multi && Array.isArray(value) && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((k) => (
            <Badge key={k} variant="secondary" className="gap-1 pl-2 pr-1 py-0.5">
              {byKey[k]?.[labelKey] || k}
              <button type="button" onClick={() => toggle(k)} className="ml-1 hover:bg-foreground/10 rounded p-0.5" data-testid={`${testId}-remove-${k}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
